/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useEffect, useMemo, useRef, useState } from "react"
import { BookingSlot } from "@medaica/common/types"
import { classNames } from "@medaica/common/services/util"
import bookingCopy from "../bookingCopy"
import { bookingColors } from "../booking-theme"

type DayCell = { key: string; weekday: string; monthDay: string; slots: BookingSlot[] }

const dayKey = (date: Date, timeZone: string): string =>
  new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).format(date)

const formatPart = (date: Date, timeZone: string, options: Intl.DateTimeFormatOptions): string =>
  new Intl.DateTimeFormat("en-US", { ...options, timeZone }).format(date)

// The full 7-day strip for the visible week (05 §4 redesign): every day renders as a cell —
// days without slots are faded, display-only. Slots group into their cell by tz-rendered day.
// Cell dates are 7 consecutive CALENDAR days in the display zone, derived by date arithmetic
// from weekStart's display date (weekStart = that zone's day-start, see pagingBase) and
// formatted from a synthetic UTC-noon instant — stepping real instants by 24h instead could
// drift an hour at a DST transition and mislabel a cell relative to the slots' own day keys.
const buildWeek = (weekStart: Date, slots: BookingSlot[], timeZone: string): DayCell[] => {
  const [year, month, day] = dayKey(weekStart, timeZone).split("-").map(Number)
  const cells: DayCell[] = []
  const byKey = new Map<string, DayCell>()
  for (let i = 0; i < 7; i++) {
    const cellNoonUtc = new Date(Date.UTC(year, month - 1, day + i, 12))
    const cell: DayCell = {
      key: dayKey(cellNoonUtc, "UTC"),
      weekday: formatPart(cellNoonUtc, "UTC", { weekday: "short" }),
      monthDay: formatPart(cellNoonUtc, "UTC", { month: "short", day: "numeric" }),
      slots: [],
    }
    cells.push(cell)
    byKey.set(cell.key, cell)
  }
  for (const slot of slots) {
    byKey.get(dayKey(slot.startUtc, timeZone))?.slots.push(slot)
  }
  return cells
}

const timeLabel = (date: Date, timeZone: string): string =>
  formatPart(date, timeZone, { hour: "numeric", minute: "2-digit", hour12: true })

// Day-strip cell skin for a non-active day: normal when it has slots, faded/non-clickable when empty.
// (Active days are styled inline with the brand color, so this only covers the inactive states.)
const dayCellStateClass = (hasSlots: boolean): string =>
  hasSlots ? "border-gray-200 bg-white" : "cursor-default border-gray-200 opacity-40"

const CHIP_BASE = "rounded-md py-2 text-center text-sm font-medium border"
const chipClass = (full: boolean, selected: boolean): string => {
  if (full) {
    return `${CHIP_BASE} border-gray-200 text-gray-400 line-through cursor-not-allowed`
  }
  if (selected) {
    return `${CHIP_BASE} border-transparent text-white`
  }
  return `${CHIP_BASE} border-gray-300 text-gray-800 hover:border-primary-400`
}

const SlotGrid = ({
  slots,
  weekStart,
  timeZone,
  selectedSlot,
  onSelect,
}: {
  slots: BookingSlot[]
  weekStart: Date
  timeZone: string
  selectedSlot: BookingSlot | null
  onSelect: (slot: BookingSlot) => void
}): ReactElement => {
  const week = useMemo(() => buildWeek(weekStart, slots, timeZone), [weekStart, slots, timeZone])
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const stripRef = useRef<HTMLDivElement | null>(null)
  const activeCellRef = useRef<HTMLButtonElement | null>(null)

  const daysWithSlots = week.filter((day) => day.slots.length > 0)
  const activeDay = daysWithSlots.find((day) => day.key === activeKey) ?? daysWithSlots[0] ?? null

  // The strip scrolls horizontally on narrow phones (minWidth below) — center the active day
  // there, or the first bookable day can sit entirely off-screen behind the scrollbar. Scrolls
  // ONLY the strip container: scrollIntoView would also scroll the page vertically when the
  // strip is below the fold, yanking a phone user past the step heading.
  useEffect(() => {
    const strip = stripRef.current
    const cell = activeCellRef.current
    if (strip && cell) {
      strip.scrollLeft = Math.max(0, cell.offsetLeft - (strip.clientWidth - cell.clientWidth) / 2)
    }
  }, [activeDay?.key])

  if (activeDay === null) {
    return <p className="text-sm text-gray-500">{bookingCopy.time.noSlots}</p>
  }

  return (
    <div>
      {/* Always-visible day strip for the whole week. Days without slots are display-only
          (faded, not clickable); only the active day is highlighted. */}
      {/* Day strip: fixed 7-across; on a narrow phone it scrolls horizontally instead of crushing
          each date label. minWidth forces the scroll only below the desktop card width, so desktop
          is unaffected. */}
      {/* `relative` makes the strip the cells' offsetParent, so the centering effect's
          cell.offsetLeft is measured within the scroll content — without it, offsetLeft would be
          measured from the page/card and the computed scrollLeft would overshoot. */}
      <div ref={stripRef} className="relative overflow-x-auto">
        <div className="grid grid-cols-7 gap-1.5" style={{ minWidth: 360 }}>
          {week.map((day) => {
            const hasSlots = day.slots.length > 0
            const active = day.key === activeDay.key
            return (
              <button
                key={day.key}
                ref={active ? activeCellRef : undefined}
                type="button"
                disabled={!hasSlots}
                aria-pressed={active}
                onClick={() => setActiveKey(day.key)}
                className={classNames("rounded-lg border py-2 text-center", active ? "" : dayCellStateClass(hasSlots))}
                style={active ? { borderColor: bookingColors.primary, backgroundColor: "#EEEEFE" } : undefined}
              >
                <div className="text-xs uppercase tracking-wide text-gray-500">{day.weekday}</div>
                <div
                  className={classNames("text-sm font-semibold", active ? "" : "text-gray-700")}
                  style={active ? { color: bookingColors.primary } : undefined}
                >
                  {day.monthDay}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {activeDay.slots.map((slot) => {
          const full = slot.capacity <= 0
          const selected = !full && selectedSlot?.startUtc.getTime() === slot.startUtc.getTime()
          const label = timeLabel(slot.startUtc, timeZone)
          // Full slots carry no "· Full" text — the struck-through, disabled chip conveys it
          // (and `disabled` announces "unavailable" to screen readers).
          let text = label
          if (selected) {
            text = `${label} ✓`
          }
          return (
            <button
              key={slot.startUtc.getTime()}
              type="button"
              disabled={full}
              onClick={() => onSelect(slot)}
              className={chipClass(full, selected)}
              style={selected ? { backgroundColor: bookingColors.primary } : undefined}
            >
              {text}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default SlotGrid
